import * as THREE from 'three';
import { AbstractProjectile, ProjectileType } from './AbstractProjectile';
import { BasicProjectile } from './BasicProjectile';
import { RapidFireProjectile } from './RapidFireProjectile';
import { ProjectilePool } from './ProjectilePool';
import { LODLevel } from '../optimization/LODSystem';
import { Config } from '../core/Config';
import { SpatialManager } from '../optimization/SpatialManager';

/**
 * Interface for objects that can be hit by projectiles
 */
export interface Damageable {
  takeDamage(amount: number): void;
  getPosition(): THREE.Vector3;
}

/**
 * Configuration settings for projectile batches
 */
interface ProjectileBatchConfig {
  batchSize: number;
  updateInterval: number;
}

/**
 * Manager for projectiles - handles creation, pooling, and efficient updating
 */
export class ProjectileManager {
  private static instance: ProjectileManager | null = null;
  
  private scene: THREE.Scene;
  private camera: THREE.Camera | null = null;
  private spatialManager: SpatialManager | null = null;
  
  // Pools for different projectile types
  private projectilePools: Map<ProjectileType, ProjectilePool<AbstractProjectile>>;
  
  // Frustum culling
  private frustum: THREE.Frustum = new THREE.Frustum();
  private frustumMatrix: THREE.Matrix4 = new THREE.Matrix4();
  private frustumUpdateInterval: number = 5; // Update frustum every 5 frames
  private frameCounter: number = 0;
  
  // Batch processing
  private batchConfig: ProjectileBatchConfig = {
    batchSize: 50, // Process up to 50 projectiles per frame
    updateInterval: 2 // Update every other frame
  };
  
  // Projectile LOD settings
  private lodDistanceNear: number = 40;
  private lodDistanceMedium: number = 80;
  private lodDistanceNearSq: number = this.lodDistanceNear * this.lodDistanceNear;
  private lodDistanceMediumSq: number = this.lodDistanceMedium * this.lodDistanceMedium;
  
  // Culling - track visible/non-visible projectiles
  private visibleProjectiles: AbstractProjectile[] = [];
  private nonVisibleProjectiles: AbstractProjectile[] = [];
  
  // Performance tracking
  private totalActiveProjectiles: number = 0;
  private visibleProjectileCount: number = 0;
  private culledProjectileCount: number = 0;
  private cullingEnabled: boolean = true;
  
  // Reusable vectors to avoid garbage collection
  private static readonly _tempVec3 = new THREE.Vector3();
  
  /**
   * Creates a new ProjectileManager
   * @param scene The scene where projectiles will be rendered
   */
  private constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.projectilePools = new Map();
    
    // Initialize pools for different projectile types with appropriate initial sizes
    this.initializeProjectilePools();
    
    console.log('ProjectileManager initialized');
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(scene?: THREE.Scene): ProjectileManager {
    if (!ProjectileManager.instance && scene) {
      ProjectileManager.instance = new ProjectileManager(scene);
    } else if (!ProjectileManager.instance && !scene) {
      throw new Error('ProjectileManager not initialized. Provide a scene on first call.');
    }
    
    return ProjectileManager.instance as ProjectileManager;
  }
  
  /**
   * Set the camera used for frustum culling
   */
  public setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }
  
  /**
   * Set the spatial manager for collision detection
   */
  public setSpatialManager(spatialManager: SpatialManager): void {
    this.spatialManager = spatialManager;
    console.log('ProjectileManager: SpatialManager set for collision detection');
  }
  
  /**
   * Initialize pools for different projectile types
   */
  private initializeProjectilePools(): void {
    console.log('ProjectileManager: Initializing projectile pools...');
    
    // Basic projectiles - most common, so largest pool
    this.projectilePools.set(
      ProjectileType.BASIC,
      new ProjectilePool<AbstractProjectile>(
        () => {
          console.log('Creating a new BasicProjectile instance');
          // Create a basic projectile directly
          const projectile = new BasicProjectile(this.scene);
          
          // Set the spatial manager if available
          if (this.spatialManager && projectile instanceof BasicProjectile) {
            projectile.setSpatialManager(this.spatialManager);
          }
          
          return projectile;
        },
        200, // Initial size
        1000, // Max size
        true // Auto expand
      )
    );
    
    console.log(`ProjectileManager: Pool for type ${ProjectileType.BASIC} initialized`);
    
    // RapidFire projectiles - need many since they fire rapidly
    this.projectilePools.set(
      ProjectileType.RAPID_FIRE,
      new ProjectilePool<AbstractProjectile>(
        () => {
          console.log('Creating a new RapidFireProjectile instance');
          // Create a rapid fire projectile
          const projectile = new RapidFireProjectile(this.scene);
          
          // Set the spatial manager if available
          if (this.spatialManager && projectile instanceof RapidFireProjectile) {
            projectile.setSpatialManager(this.spatialManager);
          }
          
          return projectile;
        },
        500, // Initial size - larger because of rapid fire
        2000, // Max size
        true // Auto expand
      )
    );
    
    console.log(`ProjectileManager: Pool for type ${ProjectileType.RAPID_FIRE} initialized`);
    
    // Other projectile types will be added as they're implemented
  }
  
  /**
   * Update the frustum for culling
   */
  private updateFrustum(): void {
    if (!this.camera) return;
    
    // Update the frustum based on current camera
    this.frustumMatrix.multiplyMatrices(
      this.camera.projectionMatrix,
      this.camera.matrixWorldInverse
    );
    this.frustum.setFromProjectionMatrix(this.frustumMatrix);
  }
  
  /**
   * Check if a point is within the frustum
   */
  private isInFrustum(position: THREE.Vector3, radius: number = 1.0): boolean {
    // Check if the position is within the camera frustum
    const sphere = new THREE.Sphere(position, radius);
    return this.frustum.intersectsSphere(sphere);
  }
  
  /**
   * Update all projectiles using batch processing and frustum culling
   */
  public update(deltaTime: number): void {
    this.frameCounter++;
    
    // Update frustum periodically
    if (this.frameCounter % this.frustumUpdateInterval === 0 && this.cullingEnabled) {
      this.updateFrustum();
      
      // Reset visible/non-visible arrays
      this.visibleProjectiles = [];
      this.nonVisibleProjectiles = [];
      
      // Categorize all active projectiles as visible or not
      this.projectilePools.forEach((pool, type) => {
        const activeProjectiles = pool.getActiveProjectiles();
        
        for (const projectile of activeProjectiles) {
          if (this.isInFrustum(projectile.getPosition())) {
            this.visibleProjectiles.push(projectile);
          } else {
            this.nonVisibleProjectiles.push(projectile);
          }
        }
      });
      
      // Update counts for performance tracking
      this.visibleProjectileCount = this.visibleProjectiles.length;
      this.culledProjectileCount = this.nonVisibleProjectiles.length;
      this.totalActiveProjectiles = this.visibleProjectileCount + this.culledProjectileCount;
    }
    
    // Update projectiles in batches
    if (this.frameCounter % this.batchConfig.updateInterval === 0) {
      // Update visible projectiles first (higher priority)
      this.updateProjectileBatch(this.visibleProjectiles, deltaTime);
      
      // Update non-visible projectiles if we have processing capacity left
      this.updateProjectileBatch(this.nonVisibleProjectiles, deltaTime, true);
    }
    
    // Alternatively, if we're not using frustum culling, update all pools directly
    if (!this.cullingEnabled) {
      this.projectilePools.forEach(pool => {
        pool.updateAll(deltaTime);
      });
    }
  }
  
  /**
   * Update a batch of projectiles
   * @param projectiles Array of projectiles to update
   * @param deltaTime Time since last update
   * @param isLowPriority Whether these are low-priority projectiles
   */
  private updateProjectileBatch(
    projectiles: AbstractProjectile[], 
    deltaTime: number,
    isLowPriority: boolean = false
  ): void {
    const batchSize = isLowPriority 
      ? Math.min(this.batchConfig.batchSize / 2, projectiles.length) 
      : Math.min(this.batchConfig.batchSize, projectiles.length);
    
    // Update a subset of projectiles each frame
    for (let i = 0; i < batchSize; i++) {
      const projectile = projectiles[i];
      if (projectile && projectile.isActive()) {
        // Update projectile
        const stillActive = projectile.update(deltaTime);
        
        // Check if we need to apply LOD
        if (stillActive && this.camera) {
          this.updateProjectileLOD(projectile);
        }
        
        // If not active anymore, release back to pool
        if (!stillActive) {
          const pool = this.getPoolForProjectile(projectile);
          if (pool) {
            pool.release(projectile);
          }
        }
      }
    }
  }
  
  /**
   * Update LOD level for a projectile based on distance from camera
   */
  private updateProjectileLOD(projectile: AbstractProjectile): void {
    if (!this.camera) return;
    
    // Get camera position
    const cameraPosition = this.camera.position;
    
    // Calculate distance squared from camera to projectile
    const distanceSq = projectile.getPosition().distanceToSquared(cameraPosition);
    
    // Determine appropriate LOD level
    let newLodLevel: LODLevel;
    
    if (distanceSq < this.lodDistanceNearSq) {
      newLodLevel = LODLevel.HIGH;
    } else if (distanceSq < this.lodDistanceMediumSq) {
      newLodLevel = LODLevel.MEDIUM;
    } else {
      newLodLevel = LODLevel.LOW;
    }
    
    // Update LOD if changed
    if (newLodLevel !== projectile.getLODLevel()) {
      projectile.setLODLevel(newLodLevel);
    }
  }
  
  /**
   * Find the pool that a projectile belongs to
   */
  private getPoolForProjectile(projectile: AbstractProjectile): ProjectilePool<AbstractProjectile> | null {
    // This is a simple implementation; in a real system we might need to use an index or Map
    for (const [_, pool] of this.projectilePools.entries()) {
      if (pool.getActiveProjectiles().includes(projectile)) {
        return pool;
      }
    }
    
    return null;
  }
  
  /**
   * Create a new projectile
   */
  public createProjectile(
    type: ProjectileType,
    position: THREE.Vector3,
    velocity: THREE.Vector3,
    damage: number = 10,
    targetPosition?: THREE.Vector3
  ): AbstractProjectile | null {
    console.log(`ProjectileManager: Creating projectile of type ${type} at position (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})`);
    
    // Get appropriate pool
    const pool = this.projectilePools.get(type);
    if (!pool) {
      console.warn(`No pool exists for projectile type ${type}`);
      return null;
    }
    
    // Get projectile from pool
    const projectile = pool.get();
    if (!projectile) {
      console.warn(`Failed to get projectile of type ${type} from pool`);
      return null;
    }
    
    // Set spatial manager if available and not already set
    if (this.spatialManager) {
      if (projectile instanceof BasicProjectile) {
        projectile.setSpatialManager(this.spatialManager);
      } else if (projectile instanceof RapidFireProjectile) {
        projectile.setSpatialManager(this.spatialManager);
        console.log('Set SpatialManager for RapidFireProjectile');
      }
    }
    
    // Activate projectile
    projectile.activate(position, velocity, damage);
    console.log(`Projectile activated successfully with velocity (${velocity.x.toFixed(2)}, ${velocity.y.toFixed(2)}, ${velocity.z.toFixed(2)})`);
    
    // Set target if provided (for homing projectiles)
    if (targetPosition) {
      projectile.setTargetPosition(targetPosition);
    }
    
    return projectile;
  }
  
  /**
   * Set batch processing configuration
   */
  public setBatchConfig(batchSize: number, updateInterval: number): void {
    this.batchConfig.batchSize = Math.max(1, batchSize);
    this.batchConfig.updateInterval = Math.max(1, updateInterval);
  }
  
  /**
   * Enable or disable frustum culling
   */
  public setCullingEnabled(enabled: boolean): void {
    this.cullingEnabled = enabled;
  }
  
  /**
   * Set LOD distance thresholds
   */
  public setLODDistances(near: number, medium: number): void {
    this.lodDistanceNear = near;
    this.lodDistanceMedium = medium;
    this.lodDistanceNearSq = near * near;
    this.lodDistanceMediumSq = medium * medium;
  }
  
  /**
   * Get performance statistics
   */
  public getStats(): {
    totalActive: number,
    visible: number,
    culled: number
  } {
    return {
      totalActive: this.totalActiveProjectiles,
      visible: this.visibleProjectileCount,
      culled: this.culledProjectileCount
    };
  }
  
  /**
   * Release all projectiles back to their pools
   */
  public releaseAllProjectiles(): void {
    this.projectilePools.forEach(pool => {
      pool.releaseAll();
    });
    
    // Clear tracking arrays
    this.visibleProjectiles = [];
    this.nonVisibleProjectiles = [];
    this.visibleProjectileCount = 0;
    this.culledProjectileCount = 0;
    this.totalActiveProjectiles = 0;
  }
} 